// =============================================================
// SurveyKind — 검사 종류 도메인 (docs/asis/기획/검사 웹사이트 세부 구성안 별첨).
// 백엔드: SurveyInstance.SurveyKind enum.
// =============================================================

export type SurveyKind = 'QUICK' | 'COMPREHENSIVE' | 'PERIODIC' | 'WORK_EVAL';

export interface SurveyKindMeta {
  kind: SurveyKind;
  /** 카드 · 헤딩 · nav 라벨 */
  label: string;
  /** 카드 short description */
  description: string;
  /** 이용자에게 안내할 대상 */
  audience: string;
  /** 회원가입 필요 여부 안내 문구 */
  memberNote: string;
  /** 결과 안내 문구 */
  resultNote: string;
  /** 카드 CTA */
  cta: string;
  /** 카드 상단 tag ("가입 없이 이용" / "회원가입 필요") */
  tag: string;
  /** 강조 카드 여부 (dark card) */
  featured: boolean;
  /** 진단 안내 '구성' 표기 — null 이면 섹션·문항 수로 계산 (2026-10-02 REQ-24·25) */
  composition: string | null;
  /** 진단 안내 '예상 소요' 표기 */
  duration: string;
}

export const SURVEY_KIND_META: Record<SurveyKind, SurveyKindMeta> = {
  QUICK: {
    kind: 'QUICK',
    label: '간편검사',
    description:
      '체크리스트로 현재 위치를 빠르게 확인합니다. 지금 바로 시작할 수 있습니다.',
    audience: '누구나',
    memberNote: '필요 없음',
    resultNote: '화면에서 바로 확인',
    cta: '간편검사 시작하기',
    tag: '가입 없이 이용',
    featured: true,
    composition: '유형 · 진행 단계 · 마음 상태',
    duration: '약 3분',
  },
  COMPREHENSIVE: {
    kind: 'COMPREHENSIVE',
    label: '종합검사',
    description:
      '처음 받는 정식 검사입니다. 3개 영역 · 8개 분야로 나의 상태를 자세히 확인합니다.',
    audience: '개인회원 · 지원기관회원',
    memberNote: '가입 후 이용',
    resultNote: '결과지 발급 · 나의 페이지 저장',
    cta: '종합검사 시작하기',
    tag: '회원가입 필요',
    featured: false,
    composition: '3개 영역 · 8개 분야',
    duration: '약 20~30분',
  },
  PERIODIC: {
    kind: 'PERIODIC',
    label: '정기검사',
    description:
      '종합검사 이후 다시 받는 검사입니다. 이전 결과와 나란히 놓고 변화를 봅니다.',
    audience: '종합검사를 마친 회원',
    memberNote: '가입 후 이용',
    resultNote: '검사 추이와 해설 제공',
    cta: '정기검사 시작하기',
    tag: '회원가입 필요',
    featured: false,
    composition: null,
    duration: '약 10~15분',
  },
  // 2026-10-31 정기검사에서 분리 — 일경험 수행 평가(자가) · 후원기관 평가는 기관 담당자가 따로 작성
  WORK_EVAL: {
    kind: 'WORK_EVAL',
    label: '일경험수행평가',
    description:
      '일경험에 참여한 뒤 나의 수행 정도를 스스로 평가합니다. 결과는 종합·정기검사 결과지 직업진로에 함께 반영됩니다.',
    audience: '종합검사를 마친 회원',
    memberNote: '가입 후 이용',
    resultNote: '결과지 직업진로에 반영',
    cta: '일경험수행평가 시작하기',
    tag: '회원가입 필요',
    featured: false,
    // 후원기관 평가는 기관 담당자가 따로 작성 → 섹션 수로 세면 응답자에게 맞지 않음
    composition: '일경험 수행 자가 평가 1개 영역',
    duration: '약 5~10분',
  },
};

/** 종류별 색 클래스 (kind-theme 를 불러온 module.scss 의 kindComprehensive · kindPeriodic · kindWorkEval). */
export function kindThemeClass(styles: Record<string, string>, kind: string | null | undefined): string | undefined {
  if (kind === 'COMPREHENSIVE') return styles.kindComprehensive;
  if (kind === 'PERIODIC') return styles.kindPeriodic;
  if (kind === 'WORK_EVAL') return styles.kindWorkEval;
  return undefined;
}

/** 종합검사를 마친 뒤에만 참여하는 검사 종류 (정기검사 · 일경험수행평가). */
export const AFTER_COMPREHENSIVE_KINDS: SurveyKind[] = ['PERIODIC', 'WORK_EVAL'];

/** URL query 값 ("quick" / "comprehensive" / "periodic") → SurveyKind. */
export function parseSurveyKindParam(raw: string | null | undefined): SurveyKind | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === 'QUICK' || upper === 'COMPREHENSIVE' || upper === 'PERIODIC' || upper === 'WORK_EVAL') {
    return upper;
  }
  return null;
}

/** 회차 목록 필터에 사용할 lowercase 표현. */
export function surveyKindParam(kind: SurveyKind): string {
  return kind.toLowerCase();
}
